import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowLeft, ArrowRight, Calendar, MapPin, Users, Loader2, Video } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au';

interface Presenter {
  name: string;
  title?: string | null;
  imageUrl?: string | null;
}

interface Workshop {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  presenterName: string | null;
  presenterTitle: string | null;
  presenters: Presenter[];
  coverImageUrl: string | null;
  startsAt: string;
  endsAt: string | null;
  locationType: string;
  location: string | null;
  capacity: number | null;
  status: string;
  topics: string[];
  registrantCount: number;
}

function fmtWhen(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true }),
  };
}

export default function WorkshopsListPage() {
  const [workshops, setWorkshops] = useState<Workshop[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios.get<{ data: { workshops: Workshop[] } }>(`${API_URL}/public/workshops`)
      .then(r => setWorkshops(r.data.data.workshops))
      .catch(e => setError(e?.response?.data?.error || e?.message || 'Failed to load workshops'));
  }, []);

  const now = Date.now();
  const upcoming = (workshops || []).filter(w => new Date(w.startsAt).getTime() >= now);
  const past = (workshops || []).filter(w => new Date(w.startsAt).getTime() < now);

  return (
    <div className="min-h-screen bg-[#262635] text-white relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-1/4 left-1/4 w-[600px] h-[600px] bg-[#5048e5]/10 rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/5 w-[500px] h-[500px] bg-[#7c3aed]/12 rounded-full blur-[150px]" />
      </div>

      <header className="relative z-10 px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#7c75f0]" />
          <span className="text-base tracking-[0.15em] font-light">RONIDOC<span className="font-semibold">AI</span></span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/demo" className="hidden md:inline-flex items-center gap-1.5 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-xs tracking-[0.15em] uppercase px-5 py-2.5 rounded-full transition">
            Book Demo
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="pt-12 pb-16 md:pt-20">
            <p className="text-[11px] md:text-[13px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-6">
              Free · Live · Online
            </p>
            <h1 className="text-[clamp(2.25rem,7vw,5.5rem)] font-extralight leading-[1.05] tracking-tight">
              <span className="block">Workshops</span>
              <span className="block font-bold bg-gradient-to-br from-white via-white to-[#bdb4ff] bg-clip-text text-transparent">
                to learn what RoniDoc can do.
              </span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Live sessions for accountants, bookkeepers and firm partners - see the platform in action,
              ask questions, walk away with practical AI workflows you can use the next day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="inline-flex items-center gap-2 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[12px] tracking-[0.15em] uppercase px-6 py-3 rounded-full transition">
                Or book a private demo
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {error && (
            <div className="border border-rose-500/30 bg-rose-500/10 rounded-2xl p-5 text-sm text-rose-200">
              {error}
            </div>
          )}

          {!workshops && !error && (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
          )}

          {workshops && upcoming.length === 0 && past.length === 0 && (
            <EmptyState />
          )}

          {upcoming.length > 0 && (
            <Section title="Upcoming">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcoming.map(ws => <WorkshopCard key={ws.id} ws={ws} />)}
              </div>
            </Section>
          )}

          {past.length > 0 && (
            <Section title="Past sessions">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {past.slice(0, 6).map(ws => <WorkshopCard key={ws.id} ws={ws} faded />)}
              </div>
            </Section>
          )}
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12">
      <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">{title}</p>
      {children}
    </section>
  );
}

function WorkshopCard({ ws, faded }: { ws: Workshop; faded?: boolean }) {
  const when = fmtWhen(ws.startsAt);
  const isFull = ws.capacity != null && ws.registrantCount >= ws.capacity;
  const isPast = new Date(ws.startsAt).getTime() < Date.now();
  const presenters = ws.presenters && ws.presenters.length > 0
    ? ws.presenters
    : (ws.presenterName ? [{ name: ws.presenterName, title: ws.presenterTitle, imageUrl: null }] : []);

  return (
    <Link
      to={`/workshops/${ws.slug}`}
      className={cn(
        'group block overflow-hidden border border-zinc-800 hover:border-[#7c75f0]/60 bg-zinc-900/30 backdrop-blur-md rounded-2xl transition-all',
        // past cards visually faded but still clickable so visitors can read the page + recording-on-request flow
        faded && 'opacity-70 hover:opacity-100'
      )}
    >
      {/* Cover - natural aspect ratio, no height cropping */}
      {ws.coverImageUrl ? (
        <div className="overflow-hidden bg-zinc-900">
          <img
            src={ws.coverImageUrl}
            alt=""
            className="block w-full h-auto group-hover:scale-[1.02] transition-transform duration-700"
          />
        </div>
      ) : (
        <div className="aspect-[16/8] bg-gradient-to-br from-[#5048e5]/20 via-zinc-900 to-[#7c3aed]/20 flex items-center justify-center">
          <Calendar className="h-10 w-10 text-[#7c75f0]/40" />
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col items-center justify-center w-16 h-16 rounded-2xl bg-[#5048e5]/15 border border-[#7c75f0]/30 flex-shrink-0">
            <span className="text-[10px] tracking-wider uppercase text-[#9c86fd]">{when.date.split(' ')[2]}</span>
            <span className="text-2xl font-bold leading-none mt-0.5">{when.date.split(' ')[1]}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold leading-snug">{ws.title}</h3>
            {ws.subtitle && <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{ws.subtitle}</p>}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-500">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> {when.date} · {when.time}
          </span>
          <span className="inline-flex items-center gap-1.5">
            {ws.locationType === 'online' ? <Video className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
            {ws.locationType === 'online' ? 'Online' : ws.location || 'TBA'}
          </span>
          {ws.capacity != null && (
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> {ws.registrantCount}/{ws.capacity}
            </span>
          )}
        </div>

        {presenters.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <div className="flex -space-x-2">
              {presenters.slice(0, 4).map((p, i) =>
                p.imageUrl ? (
                  <img key={i} src={p.imageUrl} alt={p.name} title={p.name}
                       className="h-7 w-7 rounded-full object-cover border-2 border-zinc-900 ring-1 ring-[#7c75f0]/30" />
                ) : (
                  <div key={i} title={p.name}
                       className="h-7 w-7 rounded-full bg-gradient-to-br from-[#5048e5] to-[#7c3aed] flex items-center justify-center text-[10px] font-semibold border-2 border-zinc-900">
                    {p.name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )
              )}
            </div>
            <p className="text-xs text-zinc-500 truncate">
              {presenters.length === 1
                ? <>Hosted by <span className="text-zinc-300">{presenters[0].name}</span></>
                : <>Hosted by <span className="text-zinc-300">{presenters[0].name}</span> + {presenters.length - 1} more</>
              }
            </p>
          </div>
        )}

        <div className="mt-5 flex items-center justify-between border-t border-zinc-800/60 pt-4">
          <span className={cn(
            'inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold',
            isPast ? 'text-zinc-500' : isFull ? 'text-amber-400' : 'text-[#7c75f0]'
          )}>
            {isPast ? 'Past' : isFull ? 'Full · join waitlist' : 'Open · register'}
          </span>
          <ArrowRight className="h-4 w-4 text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 transition" />
        </div>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-zinc-800 bg-zinc-900/20 rounded-2xl py-16 text-center">
      <Calendar className="mx-auto h-10 w-10 text-zinc-600" />
      <p className="mt-4 text-sm font-medium text-zinc-300">No workshops scheduled yet</p>
      <p className="mt-1 text-xs text-zinc-500">Want a session for your firm? Book a private demo instead.</p>
      <Link to="/demo" className="inline-flex items-center gap-2 mt-6 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[11px] tracking-[0.15em] uppercase px-5 py-2.5 rounded-full transition">
        Book Demo <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
